class LightningDealCartValidator {
	constructor() {
		// no-op - Static class
	}

	/**
	 * @returns {Object}:
	 *    - payload: The payload object used to update the cart items if validation found changes needed.
	 *    - variantIds: Object of variant ids changed in the update.
	 */
	static async validateCart(storefrontApiDetails) {
		if (!storefrontApiDetails) {
			console.error('No storefront api details');
			return null;
		}
		const lightningDealMap = LightningDealCartValidator.getLightningDealMap();
		const cartHasLightningDeals = LightningDealCartValidator.cartHasLightningDeals(lightningDealMap);
		if (!cartHasLightningDeals) {
			return null;
		}
		return await LightningDealCartValidator.getCartUpdates(lightningDealMap, storefrontApiDetails);
	}

	static cartHasLightningDeals(lightningDealMap) {
		const lightningDealMetaobjectIds = Object.values(lightningDealMap);
		const length = lightningDealMetaobjectIds.length;
		return length != null && length > 0;
	}

	static async getCartUpdates(lightningDealMap, storefrontApiDetails) {
		const lightningDealMetaobjectIds = Object.values(lightningDealMap);
		const cartData = await LightningDealCartValidator.fetchCartData();
		const lightningDealMetaobjects = await LightningDealCartValidator.fetchLightningDealMetaobjects(
			lightningDealMetaobjectIds,
			storefrontApiDetails
		);
		const cartUpdates = LightningDealCartValidator.generateCartUpdates(
			cartData.items,
			lightningDealMap,
			lightningDealMetaobjects
		);
		if (LightningDealCartValidator.hasCartUpdates(cartUpdates)) {
			const response = await LightningDealCartValidator.updateCartLineItems(cartUpdates.payload);
			return cartUpdates;
		}
		return null;
	}

	static async updateCartLineItems(cartLineItemUpdates) {
		const updateKeys = Object.keys(cartLineItemUpdates ?? {});
		if (updateKeys.length < 1) {
			return null;
		}
		const updates = cartLineItemUpdates;
		const body = JSON.stringify({ updates });
		try {
			const cartUpdateUrl = window.routes?.cart_update_url;
			if (!cartUpdateUrl || cartUpdateUrl === '') {
				return null;
			}
			const response = await fetch(`${cartUpdateUrl}.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: body,
			});
			if (!response.ok) {
				return null;
			}
			return await response.json();
		} catch (error) {
			console.error(error);
			return null;
		}
	}

	static async fetchLightningDealMetaobjects(lightningDealMetaobjectIds, storefrontApiDetails) {
		if (!lightningDealMetaobjectIds || !storefrontApiDetails) {
			return null;
		}
		const lightningDealMetaobjectsMap = {};
		for (const id of lightningDealMetaobjectIds) {
			const isPresent = lightningDealMetaobjectsMap.hasOwnProperty(id);
			if (isPresent) {
				continue;
			}
			lightningDealMetaobjectsMap[id] = await LightningDealCartValidator.fetchLightningDealMetaobjectData(
				id,
				storefrontApiDetails
			);
		}
		return lightningDealMetaobjectsMap;
	}

	static formatLightningDealData(lightningDealData) {
		const fields = lightningDealData?.data?.metaobject?.fields;
		if (!fields) {
			return null;
		}
		return LightningDealCartValidator.formatMetaobjectFields(fields);
	}

	static async fetchLightningDealMetaobjectData(metaobjectId, storefrontApiDetails) {
		if (!metaobjectId || !storefrontApiDetails) {
			return null;
		}
		const metaobjectByIdObj = await LightningDealCartValidator.getMetaobjectById(metaobjectId, storefrontApiDetails);
		const formattedLightningDealData = LightningDealCartValidator.formatLightningDealData(metaobjectByIdObj);
		return formattedLightningDealData;
	}

	static async fetchCartData() {
		const cartGetUrl = window.routes?.cart_url;
		if (!cartGetUrl || cartGetUrl === '') {
			return null;
		}
		const response = await fetch(`${cartGetUrl}.js`, {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		});
		try {
			return await response.json();
		} catch (error) {
			console.error(error);
			return null;
		}
	}

	static buildMetaobjectByIdQuery(metaobjectId) {
		return {
			query: `
        query GetMetaobjectById($id: ID!) {
          metaobject(id: $id) {
            id
            fields {
              key
              type
              value
            }
            handle
            type
            updatedAt
          }
        }`,
			variables: {
				id: metaobjectId,
			},
		};
	}

	static async getMetaobjectById(metaobjectId, storefrontApiDetails) {
		const shopName = window.Shopify?.shop;
		const { token, apiVersion } = storefrontApiDetails;
		if (!metaobjectId || !shopName) {
			return null;
		}
		if (!token || !apiVersion) {
			console.error('No Storefront API Token or API Version found');
			return null;
		}
		const storefrontGraphqlUrl = `https://${shopName}/api/${apiVersion}/graphql.json`;
		const graphQlQuery = LightningDealCartValidator.buildMetaobjectByIdQuery(metaobjectId);
		const response = await fetch(storefrontGraphqlUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': `${token}`,
			},
			body: JSON.stringify(graphQlQuery),
		});
		return await response.json();
	}

	/**
	 * @returns {Object<string, string>} An object where each key is a line item variant ID (string)
	 *                                   and its corresponding value is the lightning deal metaobject ID (string).
	 *                                   Format: { "<line_item_variant_id>": "<lightning_deal_metaobject_id>", ... }
	 */
	static getLightningDealMap() {
		const LIGHTNING_DEALS_MAP_ID = 'lightning-deal-map';
		const lightningDealsMapString = document.querySelector(`#${LIGHTNING_DEALS_MAP_ID}`)?.innerText;
		if (!lightningDealsMapString) {
			console.info('LightningDealCartValidator: Not validating cart.');
			return {};
		}
		try {
			return JSON.parse(lightningDealsMapString);
		} catch (error) {
			console.error(error);
			return {};
		}
	}

	static isCartItemLightningDeal(cartItem, lightningDealId) {
		if (!cartItem || lightningDealId == null) {
			return false;
		}
		const SELLING_PLAN_KEY = 'selling_plan_allocation';
		const isSubscription = cartItem.hasOwnProperty(SELLING_PLAN_KEY);
		return !isSubscription;
	}

	static isLightningDealValidTime(lightningDeal) {
		if (!lightningDeal) {
			return false;
		}
		const { start_datetime: startDatetime, end_datetime: endDatetime } = lightningDeal;
		const lightningDealStart = new Date(startDatetime);
		const lightningDealEnd = new Date(endDatetime);
		const now = new Date();
		return now >= lightningDealStart && now < lightningDealEnd;
	}

	static findLightningDealVariantIdx(lightningDealVariants, cartItemVariantId) {
		if (!cartItemVariantId || !lightningDealVariants?.length) {
			return null;
		}
		const cartItemVariantGid = `gid://shopify/ProductVariant/${cartItemVariantId}`;
		const lightningDealVariantIdx = lightningDealVariants.findIndex((variantGid) => {
			return variantGid === cartItemVariantGid;
		});
		return lightningDealVariantIdx;
	}

	static calculateAmountRemaining(lightningDeal, cartItemVariantId) {
		if (!lightningDeal || !cartItemVariantId) {
			return 0;
		}
		const {
			variant_selling_counts: variantsAmountsToSell,
			variant_amounts_sold: variantsAmountsSold,
			variants,
		} = lightningDeal;
		const lightningDealVariantIdx = LightningDealCartValidator.findLightningDealVariantIdx(variants, cartItemVariantId);
		const lightningDealAmountToSell = parseInt(variantsAmountsToSell[lightningDealVariantIdx]);
		const lightningDealAmountSold = parseInt(variantsAmountsSold[lightningDealVariantIdx]);
		const lightningDealAmountRemaining = Math.max(lightningDealAmountToSell - lightningDealAmountSold, 0);
		return lightningDealAmountRemaining;
	}

	static generateCartUpdates(cartItems, lightningDealMap, lightningDealMetaobjects) {
		if (!cartItems || !lightningDealMap || !lightningDealMetaobjects) {
			return null;
		}
		const lightningDealCartItems = LightningDealCartValidator.filterLightningDealCartItems(
			cartItems,
			lightningDealMap,
			lightningDealMetaobjects
		);
		if (lightningDealCartItems.length < 1) {
			return null;
		}
		return LightningDealCartValidator.constructCartUpdates(
			lightningDealCartItems,
			lightningDealMap,
			lightningDealMetaobjects
		);
	}

	static hasCartUpdates(cartUpdates) {
		const cartUpdatesKeys = Object.keys(cartUpdates?.payload ?? {});
		return cartUpdatesKeys.length > 0;
	}

	static formatMetaobjectFields(fields) {
		try {
			const dealData = fields.reduce((accumulator, current) => {
				const metaobjectFieldListPrefix = 'list.';
				if (current.type.startsWith(metaobjectFieldListPrefix)) {
					accumulator[current.key] = JSON.parse(current.value);
				} else {
					accumulator[current.key] = current.value;
				}
				return accumulator;
			}, {});
			return dealData;
		} catch (error) {
			console.error(error);
			return null;
		}
	}

	static filterLightningDealCartItems(cartItems, lightningDealMap, lightningDealMetaobjects) {
		if (!cartItems || !lightningDealMap || !lightningDealMetaobjects) {
			return [];
		}
		const filteredItems = cartItems.filter((cartItem) => {
			const cartItemVariantId = cartItem.variant_id;
			const lightningDealId = lightningDealMap[cartItemVariantId];
			if (!LightningDealCartValidator.isCartItemLightningDeal(cartItem, lightningDealId)) {
				return false;
			}
			const lightningDeal = lightningDealMetaobjects[lightningDealId];
			if (LightningDealCartValidator.isLightningDealSoldOut(lightningDeal)) {
				return false;
			}
			return LightningDealCartValidator.isLightningDealValidTime(lightningDeal);
		});
		return filteredItems;
	}

	static isLightningDealSoldOut(lightningDeal) {
		if (!lightningDeal) {
			return false;
		}
		const { sold_out: soldOut } = lightningDeal;
		return soldOut === 'true' || soldOut === true;
	}

	static constructCartUpdates(lightningDealCartItems, lightningDealMap, lightningDealMetaobjects) {
		const cartUpdates = {
			payload: {},
			variantIds: {},
		};
		for (const cartItem of lightningDealCartItems) {
			const itemUpdateData = LightningDealCartValidator.getLightningDealItemData(
				cartItem,
				lightningDealMap,
				lightningDealMetaobjects
			);
			if (itemUpdateData) {
				LightningDealCartValidator.addItemUpdateData(itemUpdateData, cartUpdates);
			}
		}
		return cartUpdates;
	}

	static getLightningDealItemData(cartItem, lightningDealMap, lightningDealMetaobjects) {
		const cartItemVariantId = cartItem.variant_id;
		const lightningDealId = lightningDealMap[cartItemVariantId];
		const lightningDeal = lightningDealMetaobjects[lightningDealId];
		const quantityInCart = cartItem.quantity;
		const lightningDealAmountRemaining = LightningDealCartValidator.calculateAmountRemaining(
			lightningDeal,
			cartItemVariantId
		);
		const limitPerCustomer = LightningDealCartValidator.getLightningDealLimitPerCustomer(
			lightningDeal,
			cartItemVariantId
		);
		return {
			cartItemKey: cartItem.key,
			cartItemVariantId,
			title: cartItem.title,
			quantityInCart,
			lightningDealAmountRemaining,
			limitPerCustomer,
		};
	}

	static getLightningDealLimitPerCustomer(lightningDeal, cartItemVariantId) {
		if (!lightningDeal || !cartItemVariantId) {
			return 0;
		}
		const { variants, limits_per_customer: limitsPerCustomer } = lightningDeal;
		const lightningDealVariantIdx = LightningDealCartValidator.findLightningDealVariantIdx(variants, cartItemVariantId);
		const limitPerCustomer = parseInt(limitsPerCustomer[lightningDealVariantIdx]);
		if (isNaN(limitPerCustomer)) {
			return 0;
		}
		return limitPerCustomer;
	}

	static addItemUpdateData(itemUpdateData, cartUpdates) {
		const { quantityInCart, lightningDealAmountRemaining, limitPerCustomer } = itemUpdateData;
		const lightningDealsAvailable = () =>
			lightningDealAmountRemaining > 0 && quantityInCart <= lightningDealAmountRemaining;
		const limitPerCustomerExceeded = () => limitPerCustomer > 0 && quantityInCart > limitPerCustomer;
		if (!lightningDealsAvailable()) {
			const newLimitedQuantity = Math.min(lightningDealAmountRemaining, limitPerCustomer);
			const newQuantity = limitPerCustomer > 0 ? newLimitedQuantity : lightningDealAmountRemaining;
			LightningDealCartValidator.updatePayloadWithQuantity(newQuantity, itemUpdateData, cartUpdates);
		} else if (limitPerCustomerExceeded()) {
			LightningDealCartValidator.updatePayloadWithQuantity(limitPerCustomer, itemUpdateData, cartUpdates);
		}
	}

	static updatePayloadWithQuantity(newQuantity, itemUpdateData, cartUpdates) {
		const { cartItemKey, cartItemVariantId, title, quantityInCart } = itemUpdateData;
		cartUpdates.payload[cartItemKey] = newQuantity;
		if (!Object.prototype.hasOwnProperty.call(cartUpdates.variantIds, cartItemVariantId)) {
			cartUpdates.variantIds[cartItemVariantId] = {
				title,
				newQuantity: newQuantity,
				oldQuantity: quantityInCart,
			};
		}
	}
}
